import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { chatApi, curatorOf, type Attachment, type ChatMessage, type ChatSession } from '@/api/chat';
import { uploadsApi } from '@/api/uploads';
import { usersApi } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Mascot } from '@/components/ui/Mascot';
import { useIsMobile } from '@/hooks/useIsMobile';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { ChatSidebar } from './desktop/ChatSidebar';
import { cn } from '@/lib/utils';
import { CHAT_ATTACH_CATEGORY, chatAttachError } from '@/lib/chatAttach';
import { humanizeError } from '@/lib/errors';
import { teacherAvatarOf, teacherNameOf } from '@/lib/studentFields';
import { chatUnreadKey, studentChatsQuery } from '@/hooks/useCuratorChat';
import { useChatSocket } from '@/hooks/useChatSocket';
import { useStickToBottom } from '@/hooks/useStickToBottom';
import { chatDraftKey, loadChatDraft, saveChatDraft } from '@/lib/chatDraft';
import { mergeMessages } from '@/lib/chatSocket';

/**
 * Student chat — 1:1 conversation with the assigned curator.
 *
 * Connects to chat-service over WebSocket for real-time delivery (chat
 * messages + typing/presence events); falls back to REST when the WS handshake
 * fails (e.g. dev without gateway).
 */
export function StudentChatPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);

  const qc = useQueryClient();
  // Тот же запрос и то же условие, что у входов к куратору: не раньше, чем /auth/me принесёт набор прав
  // (техдолг 295). Маршрут закрыт `RequireFeature`, но тот пропускает учётку без набора.
  const sessions = useQuery(studentChatsQuery(me));

  /**
   * ⚠️ БЕРЁМ ИМЕННО ДИАЛОГ С КУРАТОРОМ, А НЕ ПЕРВЫЙ ПОПАВШИЙСЯ.
   *
   * Стояло `sessions.data?.[0]` с пометкой «у ученика один куратор → одна
   * переписка». Допущение верное не всегда: стоит появиться второй переписке
   * (групповой, архивной), и первой в списке окажется она — вместе с её пустым
   * фото и чужим именем.
   *
   * Заказчик 07.09.2026, когда фото куратора не показалось, сказал прямо:
   * «посмотри, как берёт аватарку виджет "написать куратору"». Там ровно
   * `curatorOf` — общий выбор диалога, который ищет активный личный, а на
   * первый откатывается лишь когда другого нет.
   */
  const session = curatorOf(sessions.data);

  // The student can START the chat: if no session exists yet, open one with their
  // assigned curator (find-or-create on the backend). Fires once automatically.
  const openM = useMutation({
    mutationFn: () => chatApi.openStudentChat(),
    // ⚠️ Пустой ответ в список не кладём: `[null]` ронял весь экран на первом же чтении диалога (`curatorOf`).
    // Сервер при успехе отдаёт диалог всегда; пусто бывает у подставного ответа в проверках — оттуда и мигание
    // `studentChatRequests.test.ts` (03.10.2026): падение зависело от того, успеет ли экран перерисоваться.
    onSuccess: (s) => {
      if (s?.id) qc.setQueryData(['student', 'chats'], [s]);
    },
  });
  // ⚠️ ТОЛЬКО ПОСЛЕ УСПЕШНОГО ОТВЕТА. Стояло `!isLoading && !isError`: у отключённого запроса (набор прав ещё не
  // пришёл) `isLoading` — false, и диалог открывался бы, не спросив, есть ли он.
  useEffect(() => {
    if (sessions.isSuccess && !session && !openM.isPending && !openM.data && !openM.isError) {
      openM.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions.isSuccess, session]);

  // `isPending`, а не `isLoading`: пока запрос ждёт набора прав, он не грузится, но и ответа нет — крутилка.
  if (sessions.isPending || openM.isPending)
    return (
      <>
        <PageHeader title={t('chat.title')} />
        <Card variant="subtle" className="flex items-center justify-center gap-2 py-8 text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          {t('chat.loadingChats')}
        </Card>
      </>
    );
  if (sessions.isError) {
    return (
      <>
        <PageHeader title={t('chat.title')} />
        <Card variant="subtle" className="text-center text-error">
          {humanizeError(sessions.error)}
        </Card>
      </>
    );
  }
  if (!session) {
    return (
      <>
        <PageHeader title={t('chat.title')} />
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('chat.noCurator')}
        </Card>
      </>
    );
  }

  // `key`: другой диалог — другое окно со своим черновиком и своей лентой.
  return <ChatWindow key={session.id} session={session} meId={me?.id ?? ''} />;
}

function ChatWindow({ session, meId }: { session: ChatSession; meId: string }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const me = useAuthStore((st) => st.user);
  // Имя куратора берём из самой переписки; если сервер его не прислал —
  // подписываем ролью, а не пустотой.
  const curatorName = teacherNameOf(session) || t('chat.curator');
  /**
   * ⚠️ ФОТО КУРАТОРА ЛЕЖАЛО В ОТВЕТЕ СЕРВЕРА И НЕ ЧИТАЛОСЬ ЗДЕСЬ НИ РАЗУ. Поле
   * `curator_avatar_url` приходит с 06.09.2026, но и шапка переписки, и правая
   * колонка рисовали буквы: у каждой была своя раскладка инициалов, и ни в одну
   * фото не завели. Заказчик увидел это 07.09.2026 — на других экранах фото уже
   * было, а в чате, где куратор и есть собеседник, его не было.
   */
  const curatorAvatar = teacherAvatarOf(session); // `teacher_avatar_url`, раньше `curator_avatar_url`

  // Нужны календарю и заданиям в правой колонке. Ключ тот же, что в «Учёбе» и
  // тренажёрах, — второго запроса не будет.
  const marathonsQ = useQuery({
    queryKey: ['student', 'enrolled-marathons'],
    queryFn: () => usersApi.myEnrolledMarathons(),
  });
  const marathonIds = (marathonsQ.data ?? []).map((m) => m.id);
  const qc = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Черновик лежит на устройстве: уход с экрана и выгрузка приложения недописанное не теряют (`lib/chatDraft.ts`).
  const draftKey = chatDraftKey(me?.id, session.id);
  const [draft, setDraftState] = useState(() => loadChatDraft(draftKey));
  const setDraft = (text: string) => {
    setDraftState(text);
    saveChatDraft(draftKey, text);
  };
  const [sendError, setSendError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Presence/typing state of the OTHER side (the curator).
  const [counterpartOnline, setCounterpartOnline] = useState(false);
  const [counterpartTyping, setCounterpartTyping] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const typingStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const counterpartTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Свои неотправленные сообщения: метка → текст. По метке сервер возвращает и эхо, и отказ.
  const pending = useRef(new Map<string, string>());
  const { listRef, onListScroll, stickToBottom } = useStickToBottom([messages, counterpartTyping]);

  /** Прочитано: сервер ставит отметки, а точка на значке главной гаснет сразу — без нового запроса. */
  const markRead = useCallback(() => {
    chatApi
      .markRead(session.id, [])
      .then(() => qc.setQueryData<number>(chatUnreadKey(session.id), 0))
      .catch(() => {});
  }, [session.id, qc]);

  // История по REST: при открытии, перед каждым переподключением сокета и сразу после него.
  const catchUp = useCallback(async () => {
    const fetched = await chatApi.getHistory(session.id, 0, 50);
    setMessages((m) => mergeMessages(m, fetched));
    markRead();
  }, [session.id, markRead]);

  /** Сообщение не ушло: убираем серый пузырь и возвращаем текст в поле — набранное не пропадает молча. */
  const failPending = useCallback(
    (id: string, err: unknown) => {
      const content = pending.current.get(id);
      pending.current.delete(id);
      setMessages((m) => m.filter((mm) => mm.id !== id));
      if (content) {
        setDraftState((d) => {
          const next = d || content;
          saveChatDraft(draftKey, next);
          return next;
        });
      }
      setSendError(humanizeError(err));
    },
    [draftKey],
  );

  const onEvent = useCallback(
    (parsed: unknown) => {
      const evt = parsed as { type?: string; error?: { code?: string; client_id?: string } };

      // Presence event
      if (evt.type === 'presence') {
        const p = parsed as { user_id: string; online?: boolean };
        if (p.user_id !== meId) setCounterpartOnline(Boolean(p.online));
        return;
      }
      // Typing event
      if (evt.type === 'typing') {
        const t = parsed as { user_id: string; is_typing: boolean; room_id: string };
        if (t.room_id === session.id && t.user_id !== meId) {
          setCounterpartTyping(t.is_typing);
          // Auto-clear in case the "stop" frame is lost.
          if (counterpartTypingTimer.current) clearTimeout(counterpartTypingTimer.current);
          if (t.is_typing) {
            counterpartTypingTimer.current = setTimeout(() => setCounterpartTyping(false), 4000);
          }
        }
        return;
      }
      // Отказ сервера на наше сообщение: нельзя писать, слишком часто, не сохранилось.
      if (evt.type === 'error') {
        // Код отказа — в форму клиента API (`raw.code`), иначе причина терялась и ученик видел общее «Что-то пошло
        // не так» вместо «Слишком часто» или «В этот чат сейчас нельзя написать» (промпт 12, 18.10.2026).
        if (evt.error?.client_id) failPending(evt.error.client_id, { raw: { code: evt.error.code } });
        return;
      }
      // Прочее служебное («прочитано») пузырём не рисуем.
      if (evt.type && evt.type !== 'message') return;

      // Otherwise: a chat message.
      const msg = parsed as ChatMessage;
      if (!msg.id || msg.room_id !== session.id) return;
      if (msg.client_id) pending.current.delete(msg.client_id);
      setCounterpartTyping(false);
      setMessages((m) => {
        if (m.some((mm) => mm.id === msg.id)) return m;
        if (msg.sender_id === meId) {
          // Своё эхо встаёт на место серого пузыря: по метке, а на старом сервере — по тексту.
          const byMark = msg.client_id ? m.findIndex((mm) => mm.id === msg.client_id) : -1;
          const tmpIdx =
            byMark !== -1 ? byMark : m.findIndex((mm) => mm.id.startsWith('tmp-') && mm.content === msg.content);
          if (tmpIdx !== -1) {
            const next = m.slice();
            next[tmpIdx] = msg;
            return next;
          }
        }
        return [...m, msg];
      });
      // Ответ куратора пришёл при открытом экране — он прочитан, точке на главной загораться незачем.
      if (msg.sender_id !== meId && document.visibilityState === 'visible') markRead();
    },
    [session.id, meId, failPending, markRead],
  );

  const socket = useChatSocket({ roomId: session.id, catchUp, onEvent });

  // Seed presence state from REST.
  useEffect(() => {
    let cancelled = false;
    chatApi
      .getPresence(session.id)
      .then((p) => {
        if (!cancelled) setCounterpartOnline(p.online);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session.id]);

  // Emit typing start/stop while the student composes.
  const onDraftChange = (value: string) => {
    setDraft(value);
    socket.typing(session.id, true);
    if (typingStopTimer.current) clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => socket.typing(session.id, false), 1500);
  };

  const pushMessage = async (content: string, attachments?: Attachment[]) => {
    const msgType = attachments && attachments.length > 0 ? attachments[0].type : 'text';
    const optimistic: ChatMessage = {
      id: `tmp-${Date.now()}`,
      room_id: session.id,
      sender_id: meId,
      sender_role: 'student',
      msg_type: msgType,
      content,
      attachments,
      is_read: false,
      created_at: new Date().toISOString(),
    };
    setSendError(null);
    pending.current.set(optimistic.id, content);
    stickToBottom(); // своё сообщение всегда видно, даже если читали старое
    setMessages((m) => [...m, optimistic]);

    socket.typing(session.id, false);
    if (socket.send({ room_id: session.id, content, msg_type: msgType, attachments, client_id: optimistic.id })) return;
    try {
      const saved = await chatApi.sendMessage({ room_id: session.id, content, role: 'student', msg_type: msgType, attachments });
      pending.current.delete(optimistic.id);
      // Сообщение могло успеть прийти историей — тогда серый пузырь просто убираем.
      setMessages((m) =>
        m.some((mm) => mm.id === saved.id)
          ? m.filter((mm) => mm.id !== optimistic.id)
          : m.map((mm) => (mm.id === optimistic.id ? saved : mm)),
      );
    } catch (err) {
      failPending(optimistic.id, err);
    }
  };

  const send = async () => {
    if (!draft.trim()) return;
    const content = draft.trim();
    setDraft('');
    await pushMessage(content);
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const isImage = file.type.startsWith('image/');
      // Категория ученика, а не `lesson_media` (только сотрудники, 403) — `lib/chatAttach.ts`.
      const result = await uploadsApi.upload(file, CHAT_ATTACH_CATEGORY, file.name);
      const attachment: Attachment = {
        url: result.url,
        type: isImage ? 'image' : 'file',
        name: result.filename ?? file.name,
      };
      await pushMessage(draft.trim(), [attachment]);
      setDraft('');
    } catch (err) {
      // Surface the failure — nothing optimistic was added, so the user must know.
      setUploadError(chatAttachError(err));
    } finally {
      setUploading(false);
    }
  };

  const statusLabel = counterpartTyping ? t('chat.typing') : counterpartOnline ? t('chat.online') : t('chat.offline');

  // ── Телефон: переписка во весь экран, как в макете ─────────────────────
  //
  // ⚠️ ВЫСОТА ЗАДАЁТСЯ ЗДЕСЬ, А НЕ ЦЕПОЧКОЙ height:100%. Попытка сделать
  // прокрутку внутренней для всего приложения провалилась и была откачена
  // (сборка 1.0 (14)): без сплошной цепочки высот область получила нулевую и
  // прокрутка исчезла везде. Локальная высота одного экрана такой связи не
  // имеет: `100dvh` минус нижнее меню и отступ оболочки.
  //
  // ⚠️ ОБЕ ВЕТКИ — ОДНО ДЕРЕВО ДЛЯ REACT (техдолг 368, приём п. 367). От корня до поля ввода у телефона и у
  // компьютера одинаковые звенья: `div` → grid → chat → composer → field → text, с теми же `key`. Тогда на границе
  // 768 px (поворот телефона в Safari, ширина окна) поле остаётся тем же узлом: текст, курсор и клавиатура на
  // месте, лента не прыгает к началу. У телефона звенья grid и chat — `contents`: в раскладке их нет.
  // Держит `chatLayoutSwitchKeepsDraft.test.ts`.
  //
  // ⚠️ В ПРИЛОЖЕНИИ ВЫСОТУ ЗАДАЁТ НЕ ЭТОТ КЛАСС, А `html.native .chat-screen` в `index.css` (05.10.2026, сборка 1.1.1
  // по шнуру): экран привязан к краям окна, клавиатуру iOS отдаёт сжатием окна. `h-[calc(100dvh-9.25rem)]` остаётся
  // сайту в браузере телефона.
  if (isMobile) {
    return (
      <div className="chat-screen flex h-[calc(100dvh-9.25rem)] flex-col">
        <div key="grid" className="contents">
        <div key="chat" className="contents">
        <div key="head" className="flex flex-none items-center gap-2.5 pb-3 pt-1.5">
          <Link
            to="/app"
            aria-label={t('nav.dashboard')}
            className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
          >
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </Link>
          {/* ⚠️ НА ТЕЛЕФОНЕ БЫЛО ХУЖЕ, ЧЕМ НА КОМПЬЮТЕРЕ: не только без фото, но и
              без ИМЕНИ — в кружке стояли первые две буквы слова «Куратор», а
              подписью шло то же слово. То есть ученик не видел, с кем говорит,
              хотя имя пришло с сервера вместе с фото. Нашлось сторожем при
              разборе 07.09.2026. */}
          <Avatar name={curatorName} src={curatorAvatar} className="h-10 w-10 flex-none text-[13px]" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-[16px] font-bold text-on-surface">
              {curatorName}
            </div>
            <div
              className={cn(
                'flex items-center gap-1.5 text-[11.5px]',
                counterpartTyping ? 'text-primary' : counterpartOnline ? 'text-success' : 'text-on-surface-variant',
              )}
            >
              {counterpartOnline && !counterpartTyping && (
                <span className="h-1.5 w-1.5 rounded-full bg-success" />
              )}
              {statusLabel}
            </div>
          </div>
        </div>

        <div
          key="list"
          ref={listRef}
          onScroll={onListScroll}
          className="flex flex-1 flex-col gap-2.5 overflow-y-auto pb-2"
        >
          {messages.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
              <Mascot pose="chat" className="mascot h-32 w-auto object-contain" />
              <p className="max-w-[240px] text-[12.5px] text-on-surface-variant">{t('chat.noMessages')}</p>
            </div>
          ) : (
            messages.map((m, i) => (
              <MobileBubble
                key={m.id}
                msg={m}
                mine={m.sender_id === meId}
                showDay={dayOf(m) !== dayOf(messages[i - 1])}
              />
            ))
          )}
          {counterpartTyping && (
            <div className="self-start rounded-[22px] rounded-bl-lg bg-surface-container-low px-3.5 py-2.5 text-[13px] text-on-surface-variant">
              {t('chat.typing')}
            </div>
          )}
        </div>

        {/* Быстрые ответы из макета — пока человек ничего не набрал. */}
        {!draft.trim() && (
          <div
            key="quick"
            className="-mx-5 flex flex-none gap-1.5 overflow-x-auto px-5 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {[t('chat.quickSent'), t('chat.quickExtend')].map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => onDraftChange(q)}
                className="flex-none rounded-full bg-surface-container-low px-3.5 py-2 text-[12px] font-semibold text-on-surface"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {uploadError && (
          <p key="upErr" className="flex-none pb-1 text-[11px] text-error">
            {t('chat.uploadError', { error: uploadError })}
          </p>
        )}
        {sendError && (
          <p key="sendErr" className="flex-none pb-1 text-[11px] text-error">
            {sendError}
          </p>
        )}

        {/* Над клавиатурой поле держит сам экран (`html.native .chat-screen`), отдельного подъёма у панели нет:
            iOS уже сжимает окно, второй подъём уносил поле в середину переписки (iPhone владельца, 05.10.2026). */}
        <div key="composer" className="flex flex-none items-center gap-2">
          <input key="file" ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
          <div key="field" className="flex flex-1 items-center gap-2.5 rounded-full bg-surface-container-low px-4 py-3">
            <input
              key="text"
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
              placeholder={t('chat.messagePlaceholder')}
              className="min-w-0 flex-1 bg-transparent text-[13.5px] text-on-surface placeholder:text-on-surface-variant focus:outline-none"
            />
            <button
              key="attach"
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              aria-label={t('chat.attachTitle')}
              className="flex-none text-on-surface-variant"
            >
              <span className="material-symbols-outlined text-[18px]">
                {uploading ? 'hourglass_top' : 'attach_file'}
              </span>
            </button>
          </div>
          <button
            key="send"
            type="button"
            onClick={send}
            disabled={!draft.trim()}
            aria-label={t('common.send')}
            className="grid h-[46px] w-[46px] flex-none place-items-center rounded-full bg-primary text-on-primary disabled:opacity-45"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
          </button>
        </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StudentTopBar
        key="top"
        title={t('chat.title')}
        subtitle={t('chat.subtitle')}
        name={me?.full_name}
        avatarUrl={me?.avatar_url}
      />

      {/* ⚠️ ВЫСОТА ПО ОКНУ, А НЕ ЖЁСТКИЕ 600 ТОЧЕК. На обычном ноутбуке переписка
          занимала чуть больше половины экрана: под ней зияла пустота, а правая
          колонка уезжала ниже. Заказчик 07.09.2026: «чат как-то на пол экрана —
          неприкол».

          `items-start` убран заодно: он прижимал колонки к верху и мешал им
          вытянуться на одну высоту. */}
      <div key="grid" className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* ── Переписка ─────────────────────────────────────────────────── */}
        <div
          key="chat"
          className="flex h-[calc(100vh-10rem)] min-h-[420px] flex-col overflow-hidden rounded-[24px] bg-surface-container"
        >
          <div key="head" className="flex items-center gap-3 border-b border-outline bg-background px-5 py-4">
            {/* Общий компонент: он сам покажет фото, а без фото — буквы. Своя
                раскладка букв здесь была четвёртой копией одного и того же кода,
                и именно в ней про фото забыли (07.09.2026). */}
            <Avatar name={curatorName} src={curatorAvatar} className="h-[42px] w-[42px] flex-none text-[13px]" />
            <div className="min-w-0">
              <p className="truncate text-[14px] font-bold text-on-surface">{curatorName}</p>
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'h-[7px] w-[7px] rounded-full',
                    counterpartOnline ? 'bg-info' : 'bg-on-surface-variant/40',
                  )}
                />
                <span
                  className={cn(
                    'text-[11.5px]',
                    counterpartTyping ? 'text-primary' : 'text-on-surface-variant',
                  )}
                >
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>

          <div
            key="list"
            ref={listRef}
            onScroll={onListScroll}
            className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-5"
          >
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                <Mascot pose="chat" className="mascot h-36 w-auto object-contain" />
                <p className="max-w-xs text-[13px] text-on-surface-variant">{t('chat.noMessages')}</p>
              </div>
            ) : (
              messages.map((m, i) => (
                <Bubble
                  key={m.id}
                  msg={m}
                  mine={m.sender_id === meId}
                  // Разделитель дня — как в макете: таблетка по центру, когда
                  // дата сменилась.
                  showDay={dayOf(m) !== dayOf(messages[i - 1])}
                />
              ))
            )}
            {counterpartTyping && (
              <div className="flex items-center gap-1.5 self-start rounded-[18px] bg-background px-4 py-3">
                {[0, 1, 2].map((d) => (
                  <span key={d} className="h-1.5 w-1.5 rounded-full bg-on-surface-variant/60" />
                ))}
              </div>
            )}
          </div>

          <div key="composer" className="border-t border-outline bg-background px-[18px] py-3.5">
            {uploadError && (
              <p key="upErr" className="mb-2 text-[11.5px] text-error">
                {t('chat.uploadError', { error: uploadError })}
              </p>
            )}
            {sendError && (
              <p key="sendErr" className="mb-2 text-[11.5px] text-error">
                {sendError}
              </p>
            )}
            <div key="field" className="flex items-center gap-[11px]">
              <input key="file" ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
              <button
                key="attach"
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                title={t('chat.attachTitle')}
                className="grid h-[38px] w-[38px] flex-none place-items-center rounded-full bg-surface-container transition hover:bg-surface-container-high disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[18px] text-on-surface-variant">
                  {uploading ? 'hourglass_top' : 'attach_file'}
                </span>
              </button>
              <input
                key="text"
                value={draft}
                onChange={(e) => onDraftChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
                placeholder={t('chat.messagePlaceholder')}
                className="flex-1 rounded-full bg-surface-container px-[18px] py-3 text-[13px] text-on-surface placeholder:text-on-surface-variant focus:outline-none"
              />
              <button
                key="send"
                type="button"
                onClick={send}
                disabled={!draft.trim()}
                aria-label={t('chat.send')}
                className="grid h-[42px] w-[42px] flex-none place-items-center rounded-full bg-primary transition hover:bg-primary-hover disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[18px] text-on-primary">arrow_forward</span>
              </button>
            </div>
          </div>
        </div>

        {/* Колонка прокручивается сама: виджетов в ней больше, чем помещается в
            высоту переписки, и без своей прокрутки они растянули бы страницу,
            вернув ту же пустоту под чатом. */}
        <div key="side" className="xl:h-[calc(100vh-10rem)] xl:overflow-y-auto">
          <ChatSidebar marathonIds={marathonIds} />
        </div>
      </div>
    </div>
  );
}

/** День сообщения — по нему рисуется разделитель «Бүгін». */
function dayOf(msg?: ChatMessage): string {
  if (!msg?.created_at) return '';
  return new Date(msg.created_at).toDateString();
}

/**
 * Пузырь из макета: у своего сообщения срезан нижний правый угол, у чужого —
 * нижний левый. Своё — оранжевое, чужое — песочное.
 */
function MobileBubble({ msg, mine, showDay }: { msg: ChatMessage; mine: boolean; showDay: boolean }) {
  const { t, i18n } = useTranslation();
  const time = msg.created_at
    ? new Date(msg.created_at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
    : '';
  const isToday = dayOf(msg) === new Date().toDateString();
  const day = msg.created_at
    ? isToday
      ? t('chat.today')
      : new Date(msg.created_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })
    : '';

  return (
    <>
      {showDay && day && (
        <div className="py-1 text-center text-[10.5px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
          {day}
        </div>
      )}
      <div
        className={cn(
          'max-w-[78%] px-3.5 py-2.5',
          mine
            ? 'self-end rounded-[22px] rounded-br-lg bg-primary text-white'
            : 'self-start rounded-[22px] rounded-bl-lg bg-surface-container-low text-on-surface',
        )}
      >
        <div className="whitespace-pre-wrap break-words text-[14px] leading-[1.45]">{msg.content}</div>
        {msg.attachments?.map((a) => (
          <a
            key={a.url}
            href={a.url}
            target="_blank"
            rel="noreferrer"
            className={cn('mt-1 block truncate text-[12px] underline', mine ? 'text-white/90' : 'text-info')}
          >
            {a.name ?? a.url}
          </a>
        ))}
        {time && (
          <div className={cn('mt-1 text-[10px]', mine ? 'text-right text-white/85' : 'text-on-surface-variant')}>
            {time}
          </div>
        )}
      </div>
    </>
  );
}

function Bubble({ msg, mine, showDay }: { msg: ChatMessage; mine: boolean; showDay?: boolean }) {
  const { t, i18n } = useTranslation();
  const day = new Date(msg.created_at);
  const today = new Date();
  const yest = new Date(today.getTime() - 86400000);
  const dayLabel = day.toDateString() === today.toDateString()
    ? t('chat.today')
    : day.toDateString() === yest.toDateString()
      ? t('chat.yesterday')
      : day.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' });

  return (
    <>
      {/* Разделитель дня таблеткой по центру — как в макете. */}
      {showDay && (
        <span className="self-center rounded-full bg-surface-container-high px-3.5 py-[5px] text-[10.5px] font-semibold text-on-surface-variant">
          {dayLabel}
        </span>
      )}
      {/* Время — ПОД пузырём, как в макете. Внутри оно раздувало короткие
          реплики: у «salem!» подпись была шире самого слова. */}
      <div className={cn('flex max-w-[74%] flex-col', mine ? 'self-end items-end' : 'self-start items-start')}>
      <div
        className={cn(
          // Уголок у своего пузыря справа снизу, у чужого — слева снизу.
          'px-4 py-3 text-[13.5px] leading-[1.6]',
          mine
            ? 'rounded-[18px] rounded-br-[6px] bg-primary text-on-primary'
            : 'rounded-[18px] rounded-bl-[6px] bg-background text-on-surface',
        )}
      >
        {msg.attachments?.map((a, i) =>
          a.type === 'image' ? (
            <a key={a.id ?? i} href={a.url} target="_blank" rel="noreferrer" className="block">
              <img src={a.url} alt={a.name ?? 'image'} className="mb-1 max-h-48 rounded-button object-cover" />
            </a>
          ) : (
            <a
              key={a.id ?? i}
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className="mb-1 flex items-center gap-1 underline"
            >
              <span className="material-symbols-outlined text-[16px] leading-none">description</span>
              {a.name ?? t('chat.file')}
            </a>
          ),
        )}
        {msg.content && <span>{msg.content}</span>}
      </div>
        <p
          className={cn(
            'mx-1.5 mt-[5px] flex items-center gap-1 text-[10.5px] text-on-surface-variant',
          )}
        >
          {new Date(msg.created_at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
          {mine && !msg.id.startsWith('tmp-') && (
            <span
              className={cn('material-symbols-outlined text-[12px] leading-none', msg.is_read && 'text-info')}
              title={msg.is_read ? t('chat.read') : t('chat.sent')}
            >
              {msg.is_read ? 'done_all' : 'done'}
            </span>
          )}
        </p>
      </div>
    </>
  );
}
